/* A block of text that needs noticing — an error, a warning, a confirmation.
 *
 * Markup matches `design/components/callout`. Two things it gets right that are
 * easy to get wrong:
 *
 *   · The icon is `aria-hidden`, because it repeats what the text says. An
 *     announced "exclamation mark" before every error is noise.
 *   · The live-region role depends on the tone. A danger or warning callout is
 *     `alert`, which interrupts; an informational one is `status`, which waits
 *     for a pause. Making everything an alert trains people to ignore alerts.
 *
 * Colour never carries the meaning alone — the icon and the text both say it.
 */

import type { ReactNode } from 'react';

export type CalloutTone = 'info' | 'success' | 'warning' | 'danger';

const ICON: Record<CalloutTone, string> = {
  info: 'i',
  success: '✓',
  warning: '!',
  danger: '!',
};

export interface CalloutProps {
  readonly tone: CalloutTone;
  readonly title?: string;
  readonly children: ReactNode;
}

export function Callout({ tone, title, children }: CalloutProps) {
  const interrupts = tone === 'danger' || tone === 'warning';

  return (
    <div className={`callout callout--${tone}`} role={interrupts ? 'alert' : 'status'}>
      <span className="callout__icon" aria-hidden="true">
        {ICON[tone]}
      </span>
      <div className="callout__content">
        {title ? <p className="callout__title">{title}</p> : null}
        {/* A div, not a p. Callers legitimately pass a heading, a paragraph
            and a row of buttons — the removal confirmation does — and a <p>
            cannot contain any of them: the parser closes it early, so the
            children land OUTSIDE `.callout__body` and lose its styling. It
            was a <p> until 2026-08-19, and React had been logging it. */}
        <div className="callout__body">{children}</div>
      </div>
    </div>
  );
}
